import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

export interface User {
  id: string;
  email: string;
  displayName: string;
  role: 'owner' | 'manager';
  disabledAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Only present on the read endpoint, only for managers. */
  eventGrants?: Array<{ id: string; name: string; shortCode: string }>;
}

export interface CreateUserBody {
  email: string;
  displayName: string;
  role: 'owner' | 'manager';
  password: string;
  /** Only meaningful when role === 'manager'. */
  eventIds?: string[];
}

export interface PatchUserBody {
  displayName?: string;
  email?: string;
  role?: 'owner' | 'manager';
  /** Replace-set; omit to leave existing grants alone, [] to clear. */
  eventIds?: string[];
}

export function useUsers(opts: { includeDisabled?: boolean } = {}) {
  const qs = opts.includeDisabled ? '?includeDisabled=1' : '';
  return useQuery({
    queryKey: ['users', { includeDisabled: opts.includeDisabled ?? false }],
    queryFn: () =>
      apiFetch<{ users: User[] }>(`/api/v1/admin/users${qs}`).then((r) => r.users),
  });
}

export function useUser(userId: string) {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () =>
      apiFetch<{ user: User }>(`/api/v1/admin/users/${userId}`).then((r) => r.user),
    enabled: Boolean(userId),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserBody) =>
      apiFetch<{ user: User }>('/api/v1/admin/users', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.user),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function usePatchUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchUserBody) =>
      apiFetch<{ user: User }>(`/api/v1/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.user),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
}

export function useDisableUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ user: User }>(`/api/v1/admin/users/${userId}/disable`, {
        method: 'POST',
      }).then((r) => r.user),
    onSuccess: (_data, userId) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
}

export function useEnableUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ user: User }>(`/api/v1/admin/users/${userId}/enable`, {
        method: 'POST',
      }).then((r) => r.user),
    onSuccess: (_data, userId) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      apiFetch<void>(`/api/v1/admin/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),
    // No cache invalidation: the side-effect (tokenVersion bump) doesn't change
    // any data the Console reads.
  });
}

export interface PatchMeBody {
  displayName?: string;
  email?: string;
  /** Required when changing email. */
  currentPassword?: string;
}

export function usePatchMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchMeBody) =>
      apiFetch<{ user: User }>('/api/v1/admin/me', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.user),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useChangeMyPassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      apiFetch<void>('/api/v1/admin/me/password', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // The server re-issues the cookie; the client doesn't need to invalidate
    // ['me'] because the user identity is unchanged.
  });
}
